import type {
  ResourceListItem,
  ServiceDescriptor,
  TagsProductAdapter,
} from "../types";
import type { TagsApiModule, TagsApiMountPoint } from "./request";
import type { ProductListItem } from "@redux/slices/userProfileSlice";
import { PRODUCT_CONFIG } from "shared/navigation/SideMenu/productConfig";
import { isExtenderPopEnabled } from "@shared/config/featureFlags";

/**
 * Internal-name constants — must match `state.product.selectedProduct`
 * which stores the `internalName` from `productConfig.ts`.
 */
export const PRODUCT_IDS = {
  KontrolEdge: "Kontrol Edge",
  CtxReflector: "CTX Reflector",
  CtxReflectorV2: "CTX Reflector V2",
} as const;

export type SupportedProductId =
  (typeof PRODUCT_IDS)[keyof typeof PRODUCT_IDS];

const TAGS_API_MODULE_TO_PRODUCT_LABEL: Record<TagsApiModule, string> = {
  "kontrol-edge": PRODUCT_IDS.KontrolEdge,
  "reflector-v2": PRODUCT_IDS.CtxReflectorV2,
  "reflector-v1": PRODUCT_IDS.CtxReflector,
};

/**
 * Public accessor for the wire `module` → human product label map.
 *
 * Use this to render `module` strings (e.g. `"kontrol-edge"`) in the UI as
 * the product they belong to (e.g. `"Kontrol Edge"`). The raw wire value
 * is internal and not user-friendly. Returns the input unchanged when the
 * module is unknown so we never render `undefined` / `null`.
 */
export function productLabelForModule(
  module: TagsApiModule | string | null | undefined
): string {
  if (!module) return "";
  return (
    TAGS_API_MODULE_TO_PRODUCT_LABEL[module as TagsApiModule] ?? String(module)
  );
}

/**
 * Resolve the owning module + product label for a Tags Manager `serviceType`
 * key (e.g. `"ke-port"`, `"rv2-reflector"`). Uses the same lookup as
 * {@link resolveTagsCatalogListParams}, then maps the module to its human
 * product label. Returns `null` when the service type is not registered
 * (so callers can choose to hide instead of rendering a stub).
 */
export function productLabelForServiceType(
  serviceType: string
): { module: TagsApiModule; label: string } | null {
  const params = TAGS_CATALOG_BY_SERVICE_TYPE[serviceType];
  if (!params) return null;
  return {
    module: params.module,
    label: TAGS_API_MODULE_TO_PRODUCT_LABEL[params.module],
  };
}

const TAGS_API_MODULE_ORDER: readonly TagsApiModule[] = [
  "kontrol-edge",
  "reflector-v2",
  "reflector-v1",
];

/**
 * Product labels for Tags Manager "Used in" / filters from API
 * `resourceCountByModule`. Only modules with count > 0; labels
 * sorted alphabetically (same spirit as {@link computeProductSpread}).
 */
export function productLabelsFromResourceCountByModule(
  counts: Partial<Record<TagsApiModule, number>> | undefined
): string[] {
  if (!counts) return [];
  const labels: string[] = [];
  for (const mod of TAGS_API_MODULE_ORDER) {
    const n = counts[mod] ?? 0;
    if (n > 0) labels.push(TAGS_API_MODULE_TO_PRODUCT_LABEL[mod]);
  }
  return labels.sort((a, b) => a.localeCompare(b));
}

/* ------------------------------------------------------------------ */
/* Dummy resource generators — replace with real fetchers per service. */
/* ------------------------------------------------------------------ */

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const buildIds = (
  prefix: string,
  count: number,
  labelPrefix: string,
  statuses: string[] = ["active", "active", "provisioning", "down"]
): ResourceListItem[] =>
  Array.from({ length: count }).map((_, i) => {
    const n = String(i + 1).padStart(3, "0");
    return {
      id: `${prefix}-${n}`,
      label: `${labelPrefix} ${n}`,
      subtitle: `${prefix.toUpperCase()}-${n}`,
      status: statuses[i % statuses.length],
    };
  });

/* ------------------------------------------------------------------ */
/* Per-product service catalogs                                       */
/* ------------------------------------------------------------------ */

const KE_SERVICES: ServiceDescriptor[] = [
  { type: "ke-port", label: "Ports", singularLabel: "Port" },
  { type: "ke-internet", label: "Internet Connectivity", singularLabel: "Internet" },
  {
    type: "ke-datacenter-interconnect",
    label: "Data Center Interconnect",
    singularLabel: "DCI",
  },
  {
    type: "ke-cloud-connectivity",
    label: "Cloud Connectivity",
    singularLabel: "Cloud Connection",
  },
  {
    type: "ke-bbaas",
    label: "Backbone as a Service",
    singularLabel: "BBaaS",
  },
  // { type: "ke-bb-connector", label: "Backbone Connector", singularLabel: "BB Connector" },
  // { type: "ke-vrf", label: "VRF", singularLabel: "VRF" },
  { type: "ke-fwaas", label: "Virtual Firewall", singularLabel: "Virtual Firewall" },
  {
    type: "ke-sdwan-hub",
    label: "SD-WAN",
    singularLabel: "SD-WAN",
  },
  { type: "ke-backbone", label: "Backbone", singularLabel: "Backbone" },
  // Extended PoP is BETA (hidden in prod); the wizard entry follows the same
  // flag as its routes so prod users cannot target a type prod lacks.
  ...(isExtenderPopEnabled()
    ? [
        {
          type: "ke-extender-pop",
          label: "Extended PoP",
          singularLabel: "Extended PoP",
        },
      ]
    : []),
];

const RV2_SERVICES: ServiceDescriptor[] = [
  { type: "rv2-reflector", label: "Reflectors", singularLabel: "Reflector" },
  { type: "rv2-cluster", label: "Clusters", singularLabel: "Cluster" },
  { type: "rv2-bgp-session", label: "BGP Sessions", singularLabel: "BGP Session" },
];

const RV1_SERVICES: ServiceDescriptor[] = [
  { type: "rv1-instance", label: "Instances", singularLabel: "Instance" },
  { type: "rv1-bgp-neighbor", label: "BGP Neighbors", singularLabel: "BGP Neighbor" },
  { type: "rv1-license", label: "Licenses", singularLabel: "License" },
];

const DUMMY_RESOURCES: Record<string, ResourceListItem[]> = {
  "ke-port": buildIds("port", 24, "Port"),
  "ke-bbaas": buildIds("bb", 12, "Backbone"),
  "ke-bb-connector": buildIds("bbconn", 10, "BB Connector"),
  "ke-vrf": buildIds("vrf", 12, "VRF"),
  "ke-cloud-connectivity": buildIds("cc", 18, "Cloud Connection"),
  "ke-datacenter-interconnect": buildIds("dci", 9, "DCI"),
  "ke-internet": buildIds("inet", 11, "Internet Conn"),
  "ke-sdwan-hub": buildIds("sdwan", 8, "SD-WAN"),
  "ke-fwaas": buildIds("fwaas", 8, "Virtual Firewall"),
  "rv2-reflector": buildIds("rfl", 16, "Reflector"),
  "rv2-cluster": buildIds("clu", 6, "Cluster"),
  "rv2-bgp-session": buildIds("bgp", 22, "BGP Session"),
  "rv1-instance": buildIds("inst", 14, "Instance"),
  "rv1-bgp-neighbor": buildIds("nbr", 18, "BGP Neighbor"),
  "rv1-license": buildIds("lic", 8, "License"),
};

const dummyListResources = async (
  serviceType: string
): Promise<ResourceListItem[]> => {
  await sleep(180);
  return DUMMY_RESOURCES[serviceType] ?? [];
};

/* ------------------------------------------------------------------ */
/* Adapters                                                           */
/* ------------------------------------------------------------------ */

const kontrolEdgeAdapter: TagsProductAdapter = {
  productId: PRODUCT_IDS.KontrolEdge,
  productLabel: "Kontrol Edge",
  getServices: () => KE_SERVICES,
  listResources: (_api, _tenantId, serviceType) =>
    dummyListResources(serviceType),
};

const reflectorV2Adapter: TagsProductAdapter = {
  productId: PRODUCT_IDS.CtxReflectorV2,
  productLabel: "CTX Reflector V2",
  getServices: () => RV2_SERVICES,
  listResources: (_api, _tenantId, serviceType) =>
    dummyListResources(serviceType),
};

const reflectorV1Adapter: TagsProductAdapter = {
  productId: PRODUCT_IDS.CtxReflector,
  productLabel: "CTX Reflector",
  getServices: () => RV1_SERVICES,
  listResources: (_api, _tenantId, serviceType) =>
    dummyListResources(serviceType),
};

const ADAPTERS: Record<string, TagsProductAdapter> = {
  [PRODUCT_IDS.KontrolEdge]: kontrolEdgeAdapter,
  [PRODUCT_IDS.CtxReflectorV2]: reflectorV2Adapter,
  [PRODUCT_IDS.CtxReflector]: reflectorV1Adapter,
};

/**
 * Resolve the adapter for the given product internal name.
 * Falls back to Kontrol Edge so the UI never renders an empty service list.
 */
export function getAdapterForProduct(
  productInternalName: string | null | undefined
): TagsProductAdapter {
  if (productInternalName && ADAPTERS[productInternalName]) {
    return ADAPTERS[productInternalName];
  }
  return kontrolEdgeAdapter;
}

export function listSupportedProducts(): TagsProductAdapter[] {
  return [kontrolEdgeAdapter, reflectorV2Adapter, reflectorV1Adapter];
}

/**
 * Maps a product's internal name (from `state.product.selectedProduct`) to
 * the API mount point expected by `requests/request.ts`. Falls back to
 * `"kontrol-edge"` when no product is selected or the value is unrecognised.
 */
export function productToMount(
  productInternalName: string | null | undefined
): TagsApiMountPoint {
  if (productInternalName === PRODUCT_IDS.CtxReflectorV2) return "reflector-v2";
  if (productInternalName === PRODUCT_IDS.CtxReflector) return "ctxr";
  return "kontrol-edge";
}

/** Single Service dropdown: all `serviceType` keys across products (KE / RV2 / RV1). */
export interface MergedTagsManagerServiceOption {
  type: string;
  label: string;
  singularLabel: string;
  productLabel: string;
  productId: string;
}

const TAG_MANAGER_PRODUCT_ORDER: SupportedProductId[] = [
  PRODUCT_IDS.KontrolEdge,
  PRODUCT_IDS.CtxReflectorV2,
  PRODUCT_IDS.CtxReflector,
];

/**
 * Allowlist of UI `serviceType` keys that the **picker dropdown** exposes
 * to the user. Each entry must (1) be in the backend's wire enum
 * (`BACKEND_TO_WIRE_RESOURCE_TYPE` in
 * `chopper-backend/controllers/Tags/shared/serialize.js`), AND (2) be a
 * service we want surfaced as a tagging option in the UI today.
 *
 * Notifications-only types kept out of the picker: `rv2-bgp-session`,
 * `rv1-instance` and `rv1-license` are declared as `ServiceDescriptor`s
 * for cross-module reuse (Notifications V2 lists them), but the Tags
 * API's `tags/catalog/resources` 400s on them today — they will be
 * added here when the backend ships support, no other change needed.
 *
 * To re-enable one: confirm the backend enum + adapter cover the wire
 * `resourceType`, then add the UI `serviceType` key to this Set. No other
 * changes needed — `TAGS_CATALOG_BY_SERVICE_TYPE` already has the mapping.
 */
const BACKEND_SUPPORTED_TAGGABLE_SERVICE_TYPES: ReadonlySet<string> = new Set([
  "ke-port",
  "ke-bbaas",
  // "ke-bb-connector",
  // "ke-vrf",
  "ke-cloud-connectivity",
  "ke-datacenter-interconnect",
  "ke-internet",
  "ke-sdwan-hub",
  "ke-fwaas",
  "rv2-reflector",
  "rv2-cluster",
  "rv1-bgp-neighbor",
]);

/**
 * Maps the `ProductList` array from the user profile API (`state.userProfile.ProductList`)
 * to a `Set` of product internal names (the same keys used by `PRODUCT_IDS` / adapter
 * `productId`). Returns `null` when no filtering should be applied (empty list or every
 * product is present).
 *
 * The mapping uses `PRODUCT_CONFIG` (`apiName → internalName`) so it stays aligned
 * with the canonical product name registry.
 */
export function getAllowedProductIds(
  productList: ProductListItem[] | undefined
): Set<string> | null {
  if (!productList || productList.length === 0) return null;
  const allowed = new Set<string>();
  for (const p of productList) {
    const cfg = PRODUCT_CONFIG[p.name];
    if (cfg) allowed.add(cfg.internalName);
  }
  return allowed.size > 0 ? allowed : null;
}

export function listMergedTagsManagerServices(
  allowedProductIds?: Set<string> | null
): MergedTagsManagerServiceOption[] {
  const out: MergedTagsManagerServiceOption[] = [];
  for (const adapter of listSupportedProducts()) {
    if (allowedProductIds && !allowedProductIds.has(adapter.productId)) {
      continue;
    }
    for (const svc of adapter.getServices()) {
      if (!BACKEND_SUPPORTED_TAGGABLE_SERVICE_TYPES.has(svc.type)) {
        continue;
      }
      out.push({
        type: svc.type,
        label: svc.label,
        singularLabel: svc.singularLabel,
        productLabel: adapter.productLabel,
        productId: adapter.productId,
      });
    }
  }
  out.sort((a, b) => {
    const ia = TAG_MANAGER_PRODUCT_ORDER.indexOf(
      a.productId as SupportedProductId
    );
    const ib = TAG_MANAGER_PRODUCT_ORDER.indexOf(
      b.productId as SupportedProductId
    );
    const ra = ia === -1 ? TAG_MANAGER_PRODUCT_ORDER.length : ia;
    const rb = ib === -1 ? TAG_MANAGER_PRODUCT_ORDER.length : ib;
    return ra - rb;
  });
  return out;
}

/* ------------------------------------------------------------------ */
/* Global service catalog                                             */
/* ------------------------------------------------------------------ */

/**
 * Cross-product service lookup (label + owning product) for rendering
 * existing assignments even when the active product can't resolve them.
 */
export interface GlobalServiceEntry {
  service: ServiceDescriptor;
  product: TagsProductAdapter;
}

const GLOBAL_SERVICE_CATALOG: Record<string, GlobalServiceEntry> = (() => {
  const catalog: Record<string, GlobalServiceEntry> = {};
  for (const adapter of listSupportedProducts()) {
    for (const svc of adapter.getServices()) {
      catalog[svc.type] = { service: svc, product: adapter };
    }
  }
  return catalog;
})();

export function lookupServiceType(
  serviceType: string
): GlobalServiceEntry | null {
  return GLOBAL_SERVICE_CATALOG[serviceType] ?? null;
}

/* ------------------------------------------------------------------ */
/* Tags catalog API (GET …/tags/catalog/resources)                    */
/* ------------------------------------------------------------------ */

/**
 * Maps a Tags Manager `serviceType` (e.g. `ke-port`) to the query params
 * for {@link getTagCatalogResourcesList}. The `resourceType` query string
 * must match the backend allowlist exactly, e.g. `ke-port`, `ke-dci`,
 * `rv2-instance`, `rv1-bgp-neighbor` — not the internal `serviceType` key
 * when they differ (e.g. `ke-datacenter-interconnect` → `ke-dci`).
 */
export interface TagsCatalogListParams {
  mount: TagsApiMountPoint;
  module: TagsApiModule;
  resourceType: string;
}

/**
 * Backend `resourceType` allowlist (tags catalog query param + resource-side
 * routes): see `BACKEND_TO_WIRE_RESOURCE_TYPE` in
 * `chopper-backend/controllers/Tags/shared/serialize.js`.
 *
 * UI `serviceType` keys (e.g. `ke-datacenter-interconnect`) are mapped to
 * those wire values below. Entries for `rv2-bgp-session`, `rv1-instance`
 * and `rv1-license` remain mapped here as future-proofing — when the
 * backend ships them, gating the picker requires only adding the
 * corresponding key to `BACKEND_SUPPORTED_TAGGABLE_SERVICE_TYPES` (above).
 * Today those three are intentionally excluded from the picker because
 * `tags/catalog/resources` 400s on the unrecognised wire types.
 */
const TAGS_CATALOG_BY_SERVICE_TYPE: Record<string, TagsCatalogListParams> = {
  "ke-port": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-port",
  },
  "ke-bbaas": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-bbaas",
  },
  "ke-bb-connector": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-bb-connector",
  },
  "ke-vrf": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-vrf",
  },
  "ke-cloud-connectivity": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-cloud-connectivity",
  },
  "ke-datacenter-interconnect": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-dci",
  },
  "ke-internet": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-internet",
  },
  "ke-sdwan-hub": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-sdwan-hub",
  },
  "ke-fwaas": {
    mount: "kontrol-edge",
    module: "kontrol-edge",
    resourceType: "ke-fwaas",
  },
  /** Reflector rows in the catalog API are typed as `rv2-instance`. */
  "rv2-reflector": {
    mount: "reflector-v2",
    module: "reflector-v2",
    resourceType: "rv2-instance",
  },
  "rv2-cluster": {
    mount: "reflector-v2",
    module: "reflector-v2",
    resourceType: "rv2-cluster",
  },
  "rv1-bgp-neighbor": {
    mount: "ctxr",
    module: "reflector-v1",
    resourceType: "rv1-bgp-neighbor",
  },
  "rv2-bgp-session": {
    mount: "reflector-v2",
    module: "reflector-v2",
    resourceType: "rv2-bgp-session",
  },
  "rv1-instance": {
    mount: "ctxr",
    module: "reflector-v1",
    resourceType: "rv1-instance",
  },
  "rv1-license": {
    mount: "ctxr",
    module: "reflector-v1",
    resourceType: "rv1-license",
  },
};

/** Every Tags Manager picker `serviceType` — keep in sync with notifications `tagNotificationMap.ts`. */
export const TAGS_MANAGER_SERVICE_TYPES: readonly string[] = [
  ...KE_SERVICES,
  ...RV2_SERVICES,
  ...RV1_SERVICES,
].map((s) => s.type);

export function resolveTagsCatalogListParams(
  serviceType: string
): TagsCatalogListParams | null {
  return TAGS_CATALOG_BY_SERVICE_TYPE[serviceType] ?? null;
}

/**
 * Inverse of {@link resolveTagsCatalogListParams}: maps an API row
 * `(module, resourceType)` back to the UI `serviceType` key. Used when
 * hydrating Step 2 from `GET /tags/:id/resources` so the existing items
 * match the picker's known services.
 */
export function resolveServiceTypeFromCatalog(
  module: TagsApiModule,
  resourceType: string
): string | null {
  for (const [serviceType, params] of Object.entries(
    TAGS_CATALOG_BY_SERVICE_TYPE
  )) {
    if (params.module === module && params.resourceType === resourceType) {
      return serviceType;
    }
  }
  return null;
}
